import { Box, Paper, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";

export interface StatTileProps {
  value: ReactNode;
  label: ReactNode;
  /** Optional 12.5 px muted line under the label. */
  sub?: ReactNode;
  /** Optional action, rendered at the bottom (a pill button). */
  action?: ReactNode;
  /** Numeral size in px (design: 40 on report tiles, 54 on certificate tiles). */
  valueSize?: number;
}

export type TileTier =
  | "presidential"
  | "national"
  | "participation"
  | "incomplete";

export interface TierTileProps extends Omit<StatTileProps, "label"> {
  tier: TileTier;
  /** Defaults to the localized award name for the tier. */
  label?: ReactNode;
  /** Share of tested students (0-100 or preformatted). Builds the default "{pct} of tested" sub-line. */
  pct?: number | string;
  /** Show the 56 px tier artwork (certificates page); report tiles only carry the color bar. */
  showIcon?: boolean;
}

const TIER_NAME: Record<TileTier, string> = {
  presidential: "Presidential",
  national: "National",
  participation: "Participation",
  incomplete: "Incomplete",
};

const TIER_ICON: Partial<Record<TileTier, string>> = {
  presidential: "/assets/tiers/presidential.svg",
  national: "/assets/tiers/national.svg",
  participation: "/assets/tiers/participation.svg",
};

const barColor = (theme: Theme, tier: TileTier) => {
  const p = theme.palette;
  switch (tier) {
    case "presidential":
      return p.tierPresidential;
    case "national":
      return p.tierNational;
    case "participation":
      return p.gold;
    default:
      return p.text.disabled;
  }
};

interface BodyProps extends StatTileProps {
  icon?: string;
  bar?: string;
  tier?: TileTier;
}

const TileBody = ({
  value,
  label,
  sub,
  action,
  icon,
  bar,
  tier,
  valueSize,
}: BodyProps) => (
  <Paper
    data-tier={tier}
    sx={{
      position: "relative",
      overflow: "hidden",
      borderRadius: `${radius.card}px`,
      p: icon ? 2.5 : "18px 20px",
      pt: bar && !icon ? 3 : undefined,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 1,
      height: "100%",
    }}
  >
    {bar && (
      <Box
        data-testid="tier-bar"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 5,
          bgcolor: bar,
        }}
      />
    )}
    {icon && (
      <Box
        component="img"
        src={icon}
        alt=""
        sx={{ width: 56, height: 56, mb: 0.25 }}
      />
    )}
    <Typography
      variant="stat"
      component="div"
      sx={{ fontSize: valueSize ?? (icon ? 54 : 40) }}
    >
      {value}
    </Typography>
    <Typography
      component="div"
      sx={{
        fontSize: icon ? 13 : 12,
        fontWeight: 800,
        fontStretch: fontStretch.display,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    {sub && (
      <Typography
        component="div"
        sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary" }}
      >
        {sub}
      </Typography>
    )}
    {action && <Box sx={{ mt: "auto", pt: 0.5 }}>{action}</Box>}
  </Paper>
);

/** Report tile (design `report.tiles`): big condensed numeral, uppercase label, optional sub-line and action. */
export const StatTile = (props: StatTileProps) => <TileBody {...props} />;

/** StatTile variant with the tier's 5 px color bar and, on request, the tier artwork. */
export const TierTile = ({
  tier,
  label,
  pct,
  sub,
  showIcon = false,
  ...rest
}: TierTileProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const pctText = typeof pct === "number" ? `${pct}%` : pct;
  return (
    <TileBody
      {...rest}
      label={label ?? t(`awards.${tier}`, { defaultValue: TIER_NAME[tier] })}
      sub={
        sub ??
        (pctText !== undefined
          ? t("reports.ofTested", {
              pct: pctText,
              defaultValue: "{{pct}} of tested",
            })
          : undefined)
      }
      tier={tier}
      bar={barColor(theme, tier)}
      icon={showIcon ? TIER_ICON[tier] : undefined}
    />
  );
};

export default StatTile;
